/**
 * Combobox de medicamento — digite pra filtrar (código ou nome, sem acento),
 * setas/Enter selecionam. Substitui <select> nas telas de alto volume (208 SKUs).
 */
import { useEffect, useMemo, useRef, useState } from "react";

import type { Medicamento } from "@/types/api";

interface Props {
  medicamentos: Medicamento[];
  value: string;                     // medicamento_id selecionado ("" = nenhum)
  onChange: (id: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

function normalizar(s: string): string {
  return s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

const MAX_SUGESTOES = 50;

export function MedicamentoCombobox({
  medicamentos, value, onChange, disabled, placeholder = "digite código ou nome…",
}: Props) {
  const [texto, setTexto] = useState("");
  const [aberto, setAberto] = useState(false);
  const [destaque, setDestaque] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const selecionado = medicamentos.find((m) => m.id === value) ?? null;

  // Sincroniza o texto quando a seleção muda por fora (ex.: scanner)
  useEffect(() => {
    if (selecionado) {
      setTexto(`${selecionado.codigo_interno} · ${selecionado.descricao}`);
      setAberto(false);
    } else if (!value) {
      setTexto((t) => (aberto ? t : ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, selecionado?.id]);

  const sugestoes = useMemo(() => {
    const q = normalizar(texto.trim());
    if (!q) return medicamentos.slice(0, MAX_SUGESTOES);
    const tokens = q.split(/\s+/);
    const casa = (m: Medicamento) => {
      const alvo = normalizar(`${m.codigo_interno} ${m.descricao}`);
      return tokens.every((t) => alvo.includes(t));
    };
    const comeca = (m: Medicamento) =>
      normalizar(m.descricao).startsWith(tokens[0]) || normalizar(m.codigo_interno).startsWith(tokens[0]);
    return medicamentos
      .filter(casa)
      .sort((a, b) => Number(comeca(b)) - Number(comeca(a)) || a.descricao.localeCompare(b.descricao))
      .slice(0, MAX_SUGESTOES);
  }, [medicamentos, texto]);

  // Fecha ao clicar fora
  useEffect(() => {
    function fora(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setAberto(false);
    }
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  function digitar(t: string) {
    setTexto(t);
    setAberto(true);
    setDestaque(0);
    if (value) onChange("");           // editar o texto desfaz a seleção anterior
  }

  function escolher(m: Medicamento) {
    onChange(m.id);
    setTexto(`${m.codigo_interno} · ${m.descricao}`);
    setAberto(false);
  }

  function teclas(e: React.KeyboardEvent) {
    if (!aberto && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setAberto(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setDestaque((d) => Math.min(d + 1, sugestoes.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setDestaque((d) => Math.max(d - 1, 0));
    } else if (e.key === "Enter") {
      if (aberto && sugestoes[destaque]) {
        e.preventDefault();
        escolher(sugestoes[destaque]);
      }
    } else if (e.key === "Escape") {
      setAberto(false);
    }
  }

  return (
    <div ref={wrapRef} className="relative flex-1">
      <div className="relative">
        <input
          className="input pr-8"
          value={texto}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => digitar(e.target.value)}
          onFocus={() => setAberto(true)}
          onKeyDown={teclas}
          autoComplete="off"
        />
        {texto ? (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink"
            title="Limpar"
            onClick={() => { setTexto(""); onChange(""); setAberto(false); }}
          >
            ✕
          </button>
        ) : null}
      </div>

      {aberto && !disabled ? (
        <ul className="absolute z-40 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {sugestoes.length === 0 ? (
            <li className="px-3 py-2 text-sm text-ink-muted">Nenhum medicamento encontrado.</li>
          ) : (
            sugestoes.map((m, i) => (
              <li
                key={m.id}
                className={`cursor-pointer px-3 py-2 text-sm ${i === destaque ? "bg-anarosa-50 text-anarosa-700" : "hover:bg-gray-50"}`}
                onMouseDown={(e) => { e.preventDefault(); escolher(m); }}
                onMouseEnter={() => setDestaque(i)}
              >
                <code className="text-xs">{m.codigo_interno}</code> {m.descricao}
                {m.portaria_344 ? <span className="ml-1 text-xs text-amber-700">⚠️ 344</span> : null}
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
